'use client'

import { useState, useEffect, useRef } from 'react'
import { useSession, signOut } from 'next-auth/react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import PresenceDock from '../navigation/PresenceDock'
import { useRecentlyVisited } from '@/hooks/useRecentlyVisited'
import { useModulePrefs } from '../../stores/module-prefs.store'
import { isHrefVisible } from '@/lib/module-catalog'

// ─── Icônes SVG (Heroicons) ──────────────────────────────────────────────────

const Icons = {
  Home: () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1h-2z" />
    </svg>
  ),
  Briefcase: () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  Message: () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  ),
  Search: () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  ),
  Tasks: () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
    </svg>
  ),
  Calendar: () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  News: () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
    </svg>
  ),
  Marketplace: () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  ),
  Settings: () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  Agents: () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  Memory: () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  ),
  Knowledge: () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  ),
  Profile: () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  ),
  Modules: () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
    </svg>
  ),
  Integrations: () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  ),
  Admin: () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  ),
  Roadmap: () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
    </svg>
  ),
  Shield: () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  ),
  Gpu: () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
    </svg>
  ),
  Monitor: () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  Bell: () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
  ),
  Star: () => (
    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
    </svg>
  ),
  Time: () => (
    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  ChevronDown: () => (
    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  ),
  ChevronRight: () => (
    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  ),
  Menu: () => (
    <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  ),
  Close: () => (
    <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  Cpu: () => (
    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
    </svg>
  ),
  Database: () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
    </svg>
  ),
  Users: () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  ),
}

// ─── Styles réutilisables ──────────────────────────────────────────────────

const styles = {
  sidebar: {
    display: 'flex',
    flexDirection: 'column' as const,
    height: '100%',
    background: 'rgba(6,8,16,0.96)',
    backdropFilter: 'blur(20px)',
    userSelect: 'none' as const,
    overflow: 'hidden',
    borderRight: '1px solid rgba(79,124,255,0.06)',
    boxShadow: '0 0 40px rgba(0,0,0,0.4)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 16px 12px',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    flexShrink: 0 as const,
  },
  logo: {
    fontFamily: 'var(--font-display)',
    fontSize: '18px',
    fontWeight: 700,
    letterSpacing: '-0.02em',
    color: 'var(--text-primary)',
  },
  logoAccent: {
    color: 'var(--accent)',
  },
  subtitle: {
    fontSize: '8px',
    color: 'var(--text-muted)',
    marginTop: '2px',
    fontFamily: 'var(--font-mono)',
    letterSpacing: '0.12em',
    opacity: 0.4,
  },
  search: {
    padding: '10px 12px',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    flexShrink: 0 as const,
  },
  searchInput: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(79,124,255,0.08)',
    borderRadius: '10px',
    padding: '5px 10px',
    transition: 'border-color 0.2s',
  },
  searchInputFocus: {
    borderColor: 'var(--accent)',
  },
  searchField: {
    background: 'transparent',
    border: 'none',
    outline: 'none',
    color: 'var(--text-primary)',
    fontFamily: 'var(--font-mono)',
    fontSize: 12,
    width: '100%',
  },
  nav: {
    flex: 1,
    overflowY: 'auto' as const,
    overflowX: 'hidden' as const,
    paddingBottom: 4,
  },
  sectionHeader: {
    fontFamily: 'var(--font-mono)',
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: '0.15em',
    opacity: 0.55,
    padding: '10px 14px 3px',
    textTransform: 'uppercase' as const,
  },
  navItem: (isActive: boolean, color: string) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '5px 14px 5px 16px',
    background: isActive ? `${color}14` : 'none',
    borderLeft: `2px solid ${isActive ? color : 'transparent'}`,
    color: isActive ? 'var(--text-primary)' : 'rgba(255,255,255,0.42)',
    fontFamily: 'var(--font-mono)',
    fontSize: 12,
    textDecoration: 'none' as const,
    transition: 'all 0.15s ease',
    position: 'relative' as const,
  }),
  navIcon: (isActive: boolean, color: string) => ({
    color: isActive ? color : 'rgba(255,255,255,0.22)',
    width: 20,
    height: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  }),
  badge: (type: 'count' | 'live' | 'soon') => {
    const colors = {
      count: { bg: 'rgba(79,124,255,0.12)', color: 'var(--accent)', border: 'rgba(79,124,255,0.25)' },
      live: { bg: 'rgba(52,211,153,0.12)', color: 'var(--teal)', border: 'rgba(52,211,153,0.25)' },
      soon: { bg: 'rgba(239,68,68,0.12)', color: 'var(--coral)', border: 'rgba(239,68,68,0.25)' },
    }
    const c = colors[type] || colors.count
    return {
      fontSize: 7,
      fontWeight: 700,
      letterSpacing: '0.06em',
      padding: '1px 6px',
      borderRadius: 10,
      background: c.bg,
      color: c.color,
      border: `1px solid ${c.border}`,
      flexShrink: 0,
      lineHeight: '16px',
      minWidth: 18,
      textAlign: 'center' as const,
    }
  },
  groupButton: (isActive: boolean, color: string) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    padding: '5px 14px 5px 16px',
    background: isActive ? `${color}14` : 'none',
    // Pas de raccourci `border` ici : mêlé à `borderLeft` il effaçait la barre d'accent du groupe actif et
    // faisait avertir React (« Updating a style property during rerender (borderLeft) when a conflicting
    // property is set (border) », constaté le 3 oct. 2026). Les trois autres côtés sont mis à zéro un à un.
    borderLeft: `2px solid ${isActive ? color : 'transparent'}`,
    borderTop: 'none',
    borderRight: 'none',
    borderBottom: 'none',
    color: isActive ? 'var(--text-primary)' : 'rgba(255,255,255,0.42)',
    fontFamily: 'var(--font-mono)',
    fontSize: 12,
    cursor: 'pointer',
    textAlign: 'left' as const,
    transition: 'all 0.15s ease',
  }),
  subItem: (isActive: boolean, color: string) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '4px 14px 4px 38px',
    background: isActive ? `${color}10` : 'none',
    borderLeft: `2px solid ${isActive ? color : 'transparent'}`,
    color: isActive ? 'var(--text-primary)' : 'rgba(255,255,255,0.38)',
    fontFamily: 'var(--font-mono)',
    fontSize: 11,
    textDecoration: 'none' as const,
    transition: 'all 0.15s ease',
  }),
  footer: {
    padding: '10px 14px 12px',
    borderTop: '1px solid rgba(255,255,255,0.04)',
    flexShrink: 0 as const,
  },
  userButton: (isOpen: boolean) => ({
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 12px',
    background: isOpen ? 'rgba(255,255,255,0.04)' : 'none',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left' as const,
    borderRadius: 10,
    transition: 'all 0.15s',
  }),
  avatar: (isOpen: boolean) => ({
    width: 32,
    height: 32,
    borderRadius: '50%',
    flexShrink: 0,
    background: 'linear-gradient(135deg, var(--accent), #818cf8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'var(--font-mono)',
    fontSize: 12,
    fontWeight: 800,
    color: '#fff',
    boxShadow: isOpen ? '0 0 16px rgba(79,124,255,0.4)' : 'none',
    border: `1.5px solid ${isOpen ? 'var(--accent)' : 'transparent'}`,
    transition: 'all 0.15s',
  }),
  missionCard: {
    margin: '8px 12px 12px',
    padding: '12px 14px',
    background: 'rgba(79,124,255,0.05)',
    border: '1px solid rgba(79,124,255,0.12)',
    borderRadius: 12,
    flexShrink: 0 as const,
  },
  progressBar: {
    height: 4,
    background: 'rgba(255,255,255,0.06)',
    borderRadius: 4,
    overflow: 'hidden' as const,
    marginTop: 6,
  },
  progressFill: (progress: number, color: string) => ({
    height: '100%',
    width: `${Math.min(progress, 100)}%`,
    background: color,
    borderRadius: 4,
    transition: 'width 0.5s ease',
  }),
  systemStats: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: 4,
    marginTop: 6,
  },
  statItem: {
    fontSize: 8,
    color: 'rgba(255,255,255,0.25)',
    fontFamily: 'var(--font-mono)',
    textAlign: 'center' as const,
    padding: '2px 4px',
    background: 'rgba(255,255,255,0.02)',
    borderRadius: 4,
  },
  statValue: (color: string) => ({
    color,
    fontWeight: 600,
  }),
  notificationDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    display: 'inline-block',
    marginRight: 4,
  },
}

// ─── Données de navigation ──────────────────────────────────────────────────

export interface NavItemType {
  icon: React.ReactNode
  label: string
  href: string
  badge?: string | number
  badgeType?: 'count' | 'live' | 'soon'
}

export interface NavSectionType {
  key: string
  label: string
  color: string
  roles?: string[]
  items: NavItemType[]
}

// Sections regroupées par domaine (AppShell v0.8 — voir docs/architecture/
// command-center-ui.md, Sidebar v0.8) : reflète modules/* côté backend plutôt
// qu'une taxonomie UI indépendante. Visibilité préservée à l'identique de
// l'ancien découpage — SYSTEM reste réservé au rôle admin (roles: ['admin']),
// exactement comme SYSTÈME l'était déjà ; aucune page auparavant publique ne
// devient admin-only, et inversement.
// Exporté : source unique de navigation, réutilisée par CommandPalette.tsx
// (Ctrl+K, Slice 3) — jamais une 2e liste de destinations dupliquée.
export const NAV_SECTIONS: NavSectionType[] = [
  // Une fonction = un espace canonique : chaque entrée mène à UN hub ; les modules secondaires s'ouvrent depuis les hubs
  // (onglets) ou depuis « Modules », plutôt que de multiplier les entrées.
  {
    key: 'command',
    label: 'ACCUEIL',
    color: 'var(--accent)',
    items: [
      { icon: <Icons.Home />, label: 'Accueil', href: '/' },
      { icon: <Icons.Calendar />, label: 'Aujourd\'hui', href: '/aujourdhui' },
    ],
  },
  {
    key: 'bureau',
    label: 'BUREAU',
    color: 'var(--accent)',
    items: [
      { icon: <Icons.Monitor />, label: 'Tableau de bord', href: '/bureau' },
      { icon: <Icons.Knowledge />, label: 'Fichiers', href: '/bureau/fichiers' },
      { icon: <Icons.Message />, label: 'Messagerie', href: '/messages' },
      { icon: <Icons.Briefcase />, label: 'Relations & projets', href: '/bureau/relations' },
      { icon: <Icons.Tasks />, label: 'Tâches', href: '/bureau/tasks' },
      { icon: <Icons.Calendar />, label: 'Calendrier', href: '/bureau/calendar' },
      // Modèle fonctionnel (30 sept.) : /projets montre les MISSIONS ; les Projets sont dans Relations & projets.
      { icon: <Icons.Tasks />, label: 'Missions', href: '/projets' },
      { icon: <Icons.Modules />, label: 'Modules', href: '/bureau/modules' },
    ],
  },
  {
    key: 'marche',
    label: 'MARCHÉ',
    color: 'var(--gold)',
    items: [
      { icon: <Icons.Marketplace />, label: 'Besoins & offres', href: '/marche?tab=annonces' },
      { icon: <Icons.Marketplace />, label: 'Prix', href: '/marche?tab=prix' },
      { icon: <Icons.Modules />, label: 'Achats', href: '/marche?tab=achats' },
    ],
  },
  {
    key: 'knowledge',
    label: 'CONNAISSANCE',
    color: 'var(--gold)',
    items: [
      { icon: <Icons.Knowledge />, label: 'Connaissance', href: '/connaissance' },
    ],
  },
  {
    key: 'settings',
    label: 'PARAMÈTRES',
    color: 'var(--accent)',
    items: [
      { icon: <Icons.Admin />, label: 'Paramètres', href: '/parametres' },
      { icon: <Icons.Agents />, label: 'Agents', href: '/parametres?tab=agents' },
    ],
  },  {
    key: 'system',
    label: 'ACCÈS & RÉSEAU',
    color: 'var(--coral)',
    roles: ['admin'],
    // GPU Cluster / Monitoring pointaient vers /admin/gpu, /admin/monitoring —
    // AUCUNE des deux page n'existait (30 juillet 2026, vérifié apps/web/app).
    // Le vrai contenu vit sous /ai/agents (redirige vers /parametres?tab=agents,
    // onglets ClusterTab.tsx / DiagnosticsTab.tsx, Hub Agents Phase 3B).
    // Deep-link ?sub= ajouté à AgentsPanel (même jour) pour pointer directement
    // sur le bon sous-onglet plutôt que de rediriger vers l'onglet "Agents" par
    // défaut. Journaux retiré définitivement — aucune page ni source de
    // données identifiée nulle part dans ce dépôt.
    items: [
      { icon: <Icons.Gpu />, label: 'GPU Cluster', href: '/ai/agents?sub=cluster' },
      { icon: <Icons.Monitor />, label: 'Monitoring', href: '/ai/agents?sub=diagnostics' },
      { icon: <Icons.Shield />, label: 'IAM', href: '/admin/iam' },
      { icon: <Icons.Admin />, label: 'Administration', href: '/admin' },
    ],
  },
]

// Sous-éléments du bureau
const BUREAU_SUBITEMS: NavItemType[] = [
  { icon: <Icons.Tasks />, label: 'Tâches', href: '/bureau/tasks' },
  { icon: <Icons.Calendar />, label: 'Calendrier', href: '/bureau/calendar' },
  { icon: <Icons.Knowledge />, label: 'Fichiers', href: '/bureau/fichiers' },
  { icon: <Icons.Briefcase />, label: 'Relations & projets', href: '/bureau/relations' },
  { icon: <Icons.Message />, label: 'Messagerie', href: '/messages' },]

// Favoris intelligents / récents — DEFERRED (voir docs/architecture/
// command-center-ui.md, Sidebar v0.8, Slice 2) : nécessite un vrai tracking
// d'usage qui n'existe pas encore dans ce dépôt. Retirés plutôt que remplis
// de données d'exemple fabriquées ("Mission #142", "Rapport PDF"…) — jamais
// une donnée inventée affichée comme si elle était réelle.

// ─── Composants ──────────────────────────────────────────────────────────────


// Section Header
function SectionHeader({ label, color }: { label: string; color: string }) {
  return (
    <div style={{ ...styles.sectionHeader, color }}>
      {label}
    </div>
  )
}

// Badge
function Badge({ value, type = 'count' }: { value: string | number; type?: 'count' | 'live' | 'soon' }) {
  const style = styles.badge(type)
  return <span style={style}>{value}</span>
}

// Nav Item
function NavItem({ item, color, onClick }: {
  item: NavItemType
  color: string
  onClick?: () => void
}) {
  const pathname = usePathname()
  const isActive = item.href === '/' ? pathname === '/' : (pathname === item.href || pathname.startsWith(`${item.href}/`))

  return (
    <Link
      href={item.href}
      onClick={onClick}
      style={styles.navItem(isActive, color)}
      onMouseEnter={(e) => {
        if (!isActive) {
          e.currentTarget.style.color = 'rgba(255,255,255,0.8)'
          e.currentTarget.style.background = `${color}08`
        }
      }}
      onMouseLeave={(e) => {
        if (!isActive) {
          e.currentTarget.style.color = 'rgba(255,255,255,0.42)'
          e.currentTarget.style.background = 'none'
        }
      }}
    >
      <span style={styles.navIcon(isActive, color)}>{item.icon}</span>
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {item.label}
      </span>
      {item.badge && (
        <Badge value={item.badge} type={item.badgeType || 'count'} />
      )}
      {isActive && (
        <span style={{
          marginLeft: 4,
          width: 4,
          height: 4,
          borderRadius: '50%',
          background: color,
          boxShadow: `0 0 8px ${color}`,
          animation: 'pulse-dot 2s infinite',
        }} />
      )}
    </Link>
  )
}

// Nav Group (repliable)
function NavGroup({
  icon,
  label,
  color,
  basePath,
  items,
}: {
  icon: React.ReactNode
  label: string
  color: string
  basePath: string
  items: NavItemType[]
}) {
  const pathname = usePathname()
  const inSection = pathname === basePath || pathname.startsWith(`${basePath}/`)
  const [open, setOpen] = useState(inSection)

  useEffect(() => {
    if (inSection) setOpen(true)
  }, [inSection])

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        style={styles.groupButton(inSection, color)}
        onMouseEnter={(e) => {
          if (!inSection) {
            e.currentTarget.style.color = 'rgba(255,255,255,0.8)'
            e.currentTarget.style.background = `${color}08`
          }
        }}
        onMouseLeave={(e) => {
          if (!inSection) {
            e.currentTarget.style.color = 'rgba(255,255,255,0.42)'
            e.currentTarget.style.background = 'none'
          }
        }}
      >
        <span style={styles.navIcon(inSection, color)}>{icon}</span>
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
        </span>
        <span style={{ fontSize: 9, opacity: 0.5, display: 'flex', alignItems: 'center' }}>
          {open ? <Icons.ChevronDown /> : <Icons.ChevronRight />}
        </span>
      </button>
      <div style={{
        overflow: 'hidden',
        maxHeight: open ? `${items.length * 32 + 16}px` : 0,
        opacity: open ? 1 : 0,
        transition: 'max-height 0.25s ease, opacity 0.2s ease',
      }}>
        {open && items.map((it) => {
          const active = pathname === it.href || pathname.startsWith(it.href + '/')
          return (
            <Link
              key={it.href}
              href={it.href}
              style={styles.subItem(active, color)}
              onMouseEnter={(e) => {
                if (!active) e.currentTarget.style.color = 'rgba(255,255,255,0.75)'
              }}
              onMouseLeave={(e) => {
                if (!active) e.currentTarget.style.color = 'rgba(255,255,255,0.38)'
              }}
            >
              <span style={{ ...styles.navIcon(active, color), width: 16, height: 16 }}>
                {it.icon}
              </span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {it.label}
              </span>
              {it.badge && <Badge value={it.badge} type={it.badgeType || 'count'} />}
            </Link>
          )
        })}
      </div>
    </>
  )
}

// User Menu
function SidebarUserButton() {
  const { data: session, status } = useSession()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const away = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', away)
    return () => document.removeEventListener('mousedown', away)
  }, [])

  if (status === 'loading') {
    return <div style={{ height: 40, display: 'flex', alignItems: 'center', padding: '0 14px' }}>
      <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
    </div>
  }

  if (!session) {
    return (
      <Link href="/login" style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 14px', textDecoration: 'none',
        fontFamily: 'var(--font-mono)', fontSize: 11,
        color: 'rgba(255,255,255,0.5)', borderTop: '1px solid rgba(255,255,255,0.06)',
        transition: 'all 0.15s',
      }}>
        <span style={{ fontSize: 14 }}>⇒</span> Connexion
      </Link>
    )
  }

  const name = session.user?.name ?? session.user?.email ?? '?'
  const initials = name.slice(0, 2).toUpperCase()
  const role = (session.user as any)?.role ?? 'USER'
  const groups = (session.user as any)?.groups ?? []
  const roleColor = role === 'ADMIN' ? 'var(--coral)' : role === 'VENDEUR' ? 'var(--gold)' : 'var(--teal)'
  const email = session.user?.email ?? ''
  const isAdmin = groups.some((g: string) =>
    ['admin', 'amiral_admins', 'authentik admins'].includes(g.toLowerCase().trim())
  )

  return (
    <div ref={ref} style={{ position: 'relative', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
      {open && (
        <div style={{
          position: 'absolute', bottom: 'calc(100% + 6px)', left: 8, right: 8,
          zIndex: 400,
          background: 'rgba(6,8,16,0.95)', backdropFilter: 'blur(20px)',
          border: '1px solid rgba(79,124,255,0.15)', borderRadius: 12,
          boxShadow: '0 -8px 40px rgba(0,0,0,0.6)',
          overflow: 'hidden', animation: 'fadeUp 0.15s ease-out',
        }}>
          <div style={{ padding: '12px 14px 10px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                background: 'linear-gradient(135deg, var(--accent), #818cf8)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 800,
                color: '#fff', boxShadow: '0 0 20px rgba(79,124,255,0.3)',
              }}>{initials}</div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-primary)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {name}
                </div>
                <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'var(--font-mono)' }}>
                  {email}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: roleColor, display: 'inline-block', boxShadow: `0 0 8px ${roleColor}80` }} />
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 8, color: roleColor, letterSpacing: '0.1em' }}>{role}</span>
                </div>
              </div>
            </div>
          </div>
          <div style={{ padding: '4px 0' }}>
            <MenuItem icon={<Icons.Profile />} label="Mon profil" href="/profil" onClose={() => setOpen(false)} />
            <MenuItem icon={<Icons.Settings />} label="Paramètres" href="/parametres" onClose={() => setOpen(false)} />
            <MenuItem icon={<Icons.Memory />} label="Mémoire IA" href="/ai/memory" onClose={() => setOpen(false)} />
            <MenuItem icon={<Icons.Agents />} label="Agents" href="/ai/agents" onClose={() => setOpen(false)} />
            {isAdmin && (
              <>
                <div style={{ height: 1, background: 'rgba(255,255,255,0.05)', margin: '3px 10px' }} />
                <MenuItem icon={<Icons.Admin />} label="Administration" href="/admin" onClose={() => setOpen(false)} highlight />
                <MenuItem icon={<Icons.Shield />} label="IAM Control" href="/admin/iam" onClose={() => setOpen(false)} highlight />
              </>
            )}
            <div style={{ height: 1, background: 'rgba(255,255,255,0.05)', margin: '3px 10px' }} />
            <button onClick={() => { setOpen(false); signOut({ callbackUrl: '/login' }) }} style={{
              width: '100%', textAlign: 'left', padding: '7px 14px',
              background: 'none', border: 'none', fontFamily: 'var(--font-mono)',
              fontSize: 11, color: 'var(--coral)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 8,
              transition: 'all 0.15s',
            }}>
              <span style={{ fontSize: 12, opacity: 0.7 }}>⏻</span> Déconnexion
            </button>
          </div>
        </div>
      )}

      <button onClick={() => setOpen((o) => !o)} style={styles.userButton(open)}
        onMouseEnter={(e) => { if (!open) e.currentTarget.style.background = 'rgba(255,255,255,0.03)' }}
        onMouseLeave={(e) => { if (!open) e.currentTarget.style.background = 'none' }}
      >
        <div style={styles.avatar(open)}>{initials}</div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'rgba(255,255,255,0.75)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {name}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 1 }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: roleColor, display: 'inline-block' }} />
            <span style={{ fontSize: 8, color: 'rgba(255,255,255,0.25)', fontFamily: 'var(--font-mono)' }}>{role}</span>
          </div>
        </div>
        <span style={{ fontSize: 8, color: 'rgba(255,255,255,0.2)', flexShrink: 0 }}>
          {open ? '▼' : '▲'}
        </span>
      </button>
    </div>
  )
}

function MenuItem({ icon, label, href, onClose, highlight }: {
  icon: React.ReactNode
  label: string
  href: string
  onClose: () => void
  highlight?: boolean
}) {
  return (
    <Link href={href} onClick={onClose} style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '7px 14px', textDecoration: 'none',
      fontFamily: 'var(--font-mono)', fontSize: 11,
      color: highlight ? 'var(--gold)' : 'rgba(255,255,255,0.55)',
      transition: 'all 0.1s',
    }}
    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.color = '#fff' }}
    onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = highlight ? 'var(--gold)' : 'rgba(255,255,255,0.55)' }}>
      <span style={{ width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.6, flexShrink: 0 }}>
        {icon}
      </span>
      {label}
    </Link>
  )
}

// ─── Composant principal ─────────────────────────────────────────────────────

export default function Sidebar() {
  const { data: session } = useSession()
  const [searchQuery, setSearchQuery] = useState('')
  const pathname = usePathname()

  const groups: string[] = session?.user?.groups ?? []
  const isAdmin = groups.some((g: string) =>
    ['admin', 'amiral_admins', 'authentik admins'].includes(g.toLowerCase().trim())
  )

  // Badges dynamiques — comptes réels, jamais des nombres fabriqués (Missions
  // actives / Agents en ligne / Messages non lus). Absent de liveBadges = pas
  // de badge affiché, jamais une valeur par défaut inventée. "Journaux" reste
  // volontairement sans badge : aucune route de comptage n'existe pour les
  // journaux admin — mieux vaut aucun badge qu'un badge inventé. Messagerie
  // utilise désormais /api/chat/conversations (proxy session/cookie, fiable
  // sur toutes les pages) — PAS chatApi.getConversations() du chat-client
  // existant, dont l'auth dépend d'un token localStorage synchronisé
  // uniquement par MessagesShell.tsx (visite de /messages).
  const [liveBadges, setLiveBadges] = useState<Record<string, number>>({})
  const ACTIVE_MISSION_STATUSES = ['NEW', 'ANALYSIS', 'PLANNING', 'EXECUTION', 'VALIDATION', 'WAITING_USER']

  useEffect(() => {
    if (!session) return
    const refresh = () => {
      fetch('/api/missions?limit=500')
        .then(r => (r.ok ? r.json() : []))
        .then((missions: { status: string }[]) => {
          const active = Array.isArray(missions) ? missions.filter(m => ACTIVE_MISSION_STATUSES.includes(m.status)).length : 0
          setLiveBadges(b => ({ ...b, '/projets': active }))
        })
        .catch(() => {})
      fetch('/api/agents')
        .then(r => (r.ok ? r.json() : null))
        .then((d: { agents?: { status: string }[] } | null) => {
          const online = Array.isArray(d?.agents) ? d.agents.filter(a => a.status === 'ONLINE').length : 0
          setLiveBadges(b => ({ ...b, '/ai/agents': online }))
        })
        .catch(() => {})
      fetch('/api/chat/conversations')
        .then(r => (r.ok ? r.json() : []))
        .then((conversations: { unreadCount?: number }[]) => {
          const unread = Array.isArray(conversations)
            ? conversations.reduce((n, c) => n + (c.unreadCount ?? 0), 0)
            : 0
          setLiveBadges(b => ({ ...b, '/messages': unread }))
        })
        .catch(() => {})
    }
    refresh()
    const iv = setInterval(refresh, 30_000)
    return () => clearInterval(iv)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session])

  // Filtrer les sections selon le rôle
  // Modules désactivés par l'usager : retirés du menu (jamais les pages elles-mêmes).
  const disabledModules = useModulePrefs((s) => s.disabled)
  const loadModulePrefs = useModulePrefs((s) => s.load)
  useEffect(() => { if (session) void loadModulePrefs() }, [session, loadModulePrefs])

  const visibleSections = NAV_SECTIONS.filter((section) => {
    if (!section.roles) return true
    if (!session) return false
    return section.roles.some((r) => groups.includes(r))
  }).map((section) => ({
    ...section,
    items: section.items.filter((it) => isHrefVisible(disabledModules, it.href)),
  })).filter((section) => section.items.length > 0)
  const bureauSubItems = BUREAU_SUBITEMS.filter((it) => isHrefVisible(disabledModules, it.href))

  // Filtrer par recherche
  const filteredSections = searchQuery.trim()
    ? visibleSections
        .map((section) => ({
          ...section,
          items: section.items.filter((item) =>
            item.label.toLowerCase().includes(searchQuery.toLowerCase())
          ),
        }))
        .filter((section) => section.items.length > 0)
    : visibleSections

  // Accès fréquent — classé fréquence puis récence (localStorage, voir useRecentlyVisited),
  // jamais des entrées d'exemple fabriquées (contrairement à l'ancien
  // FAVORITES/RECENT retiré au Slice 1). Résolu contre visibleSections (déjà
  // filtré par rôle) — jamais NAV_SECTIONS brut — et jamais la page courante,
  // déjà indiquée par l'état actif du nav item lui-même.
  const recentHrefs = useRecentlyVisited()
  const navByHref = new Map(visibleSections.flatMap((s) => s.items.map((it) => [it.href, it] as const)))
  const recentItems = recentHrefs
    .filter((h) => h !== pathname && navByHref.has(h))
    .map((h) => navByHref.get(h)!)
    .slice(0, 4)

  // La fermeture du tiroir mobile est gérée par AppShell (il enveloppe la sidebar) : rien à faire ici.
  const closeMobile = () => {}

  // Contenu de la sidebar (partagé entre desktop et mobile)
  const sidebarContent = (
    <div style={styles.sidebar}>
      {/* Le logo vit dans la barre du haut (Topbar) : un seul, depuis que la navigation ne recouvre plus la barre. */}

      {/* Barre de recherche */}
      <div style={styles.search}>
        <div style={styles.searchInput}
          onFocus={(e) => e.currentTarget.style.borderColor = 'var(--accent)'}
          onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(79,124,255,0.08)'}
        >
          <Icons.Search />
          <input
            type="text"
            placeholder="Rechercher..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={styles.searchField}
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} style={{
              background: 'none', border: 'none', color: 'var(--text-muted)',
              cursor: 'pointer', fontSize: 14,
            }}>✕</button>
          )}
        </div>
      </div>

      {/* Navigation */}
      <div style={styles.nav}>
        {filteredSections.map((section) => (
          <div key={section.key}>
            <SectionHeader label={section.label} color={section.color} />
            {section.items.map((item) => {
              // /bureau est le cockpit : un lien simple. Ses sous-espaces (fichiers, tâches, calendrier…) ont déjà leur entrée.
              const liveCount = liveBadges[item.href]
              const itemWithLiveBadge = liveCount !== undefined
                ? { ...item, badge: liveCount > 0 ? liveCount : undefined, badgeType: 'count' as const }
                : item
              return (
                <NavItem
                  key={item.href}
                  item={itemWithLiveBadge}
                  color={section.color}
                  onClick={closeMobile}
                />
              )
            })}
          </div>
        ))}

        {recentItems.length > 0 && (
          <div>
            <SectionHeader label="ACCÈS FRÉQUENT" color="rgba(255,255,255,0.25)" />
            {recentItems.map((item) => (
              <NavItem key={`recent-${item.href}`} item={item} color="rgba(255,255,255,0.4)" onClick={closeMobile} />
            ))}
          </div>
        )}
      </div>

      {/* Presence Dock */}
      <PresenceDock />

      {/* Profil utilisateur */}
      <SidebarUserButton />
    </div>
  )

  // Rendu
  //
  // La sidebar est un élément DANS LE FLUX : elle remplit la cellule que lui donne AppShell (colonne de
  // 240 px sur le bureau, tiroir sur mobile) et n'en sort pas. Elle était auparavant `position: fixed`,
  // 280 px de large, du haut au bas de la fenêtre : retirée de la grille, elle recouvrait les 40 px de
  // gauche du contenu (colonne de 240 px) ainsi que la gauche de la barre du haut et du pied de page
  // (constaté le 3 oct. 2026 : titre et fil d'Ariane coupés à gauche). La largeur n'est plus décidée
  // ici : une seule source, AppShell.
  return (
    <>
      <aside style={{ width: '100%', height: '100%', minHeight: 0, minWidth: 0, overflow: 'hidden' }}>
        {sidebarContent}
      </aside>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pulse-dot { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.4; transform: scale(0.8); } }
      `}</style>
    </>
  )
}
